import {
  type UseQueryResult,
  useQueries,
  useQuery,
} from "@tanstack/react-query";
import { lazy, type RefObject, Suspense, useCallback, useMemo } from "react";
import styled from "styled-components";
import { ApiError, reportsApi } from "@/api/client";
import { type SeriesPoint, seriesPoints } from "@/api/recordsSeries";
import {
  partitionStatesShare,
  type WindowSeriesJson,
  windowChartData,
  windowChartNotes,
} from "@/api/windowsSeries";
import { chartLines, hasCounts } from "@/components/chartLines";
import {
  chartSlices,
  hasShares,
  type ShareData,
} from "@/components/chartSlices";
import { SkeletonBlock } from "@/components/Skeleton";
import { useNearViewport } from "@/hooks/useNearViewport";
import { momentLabel } from "@/utils/moments";
import {
  type ChartFence,
  type PieChart,
  parseChartFence,
  type RecordsChart,
  type SeriesChart,
  seriesFreshnessMs,
  type WindowsChart,
} from "./chartFence";

// A json:chart fence drawn (TTTL-259, TTTL-304): the block reads the
// reference the model wrote, fetches the records series of each pipeline
// it names and draws the lines on one plot, or, for a pie, reads each
// series at one moment and draws the ring. Chart.js mounts lazily, like
// the code viewer: a conversation may hold no chart at all. A fence the
// reader refuses shows as the code it is, with the reason on one line;
// the engine's own message stands in for a series it cannot give — under
// the plot when the other pipelines' series came, in its place when none
// did. Nothing here imports the chart module: Chart.js first loads with
// the chart itself.
// The series are fetched only once the block comes near the viewport: the
// chat window mounts a few rows past the visible ones, and one answer may
// hold several charts, so a long conversation would otherwise ask the
// engine for series nobody is looking at.

const LazySeriesChart = lazy(() =>
  import("@/components/RecordsSeriesChart").then((m) => ({
    default: m.RecordsSeriesChart,
  })),
);

const LazyShareChart = lazy(() =>
  import("@/components/RecordsShareChart").then((m) => ({
    default: m.RecordsShareChart,
  })),
);

export function ChartBlock({
  code,
  fallback,
}: {
  code: string;
  /** How the raw fence renders when it cannot be drawn. */
  fallback: (note: string) => React.ReactNode;
}) {
  const read = useMemo(() => parseChartFence(code), [code]);
  if ("error" in read) return <>{fallback(read.error)}</>;
  const chart = read.chart;
  if (chart.kind === "windows") return <WindowsChartBlock chart={chart} />;
  return chart.kind === "pie" ? (
    <ShareChartBlock chart={chart} />
  ) : (
    <RecordsChartBlock chart={chart} />
  );
}

/** The pipelines' fetches read together: whether any is still on its
 *  way, the series of those that came, and the engine's word on each that
 *  did not — the pipeline named when the chart holds several. */
interface ChartSeries {
  pending: boolean;
  fetched: ReadonlyMap<string, SeriesPoint[]>;
  problems: string[];
}

function combineSeries(
  chart: SeriesChart,
  results: UseQueryResult<SeriesPoint[]>[],
): ChartSeries {
  const fetched = new Map<string, SeriesPoint[]>();
  const problems: string[] = [];
  results.forEach((r, i) => {
    const pipeline = chart.pipelines[i] ?? "";
    if (r.data !== undefined) fetched.set(pipeline, r.data);
    else if (r.isError) {
      const problem = seriesProblem(r.error);
      problems.push(
        chart.pipelines.length > 1 ? `${pipeline}: ${problem}` : problem,
      );
    }
  });
  return { pending: results.some((r) => r.isPending), fetched, problems };
}

/** How many moments to ask the engine for: the fence's own count for the
 *  lines, the route's default for a pie. A pie draws one moment, the last
 *  watched in the window, but asks for the whole series to find it: the
 *  engine samples the window evenly and a moment that falls just after a
 *  redeploy, before its first snapshot, is null, so one sample at the
 *  window's end could miss a pipeline the window did watch. The series
 *  also serves a line chart of the same window from the cache. */
function pointsOf(chart: SeriesChart): number | undefined {
  return chart.kind === "pie" ? undefined : chart.points;
}

/** One fetch per pipeline, the same window for each, cached by pipeline
 *  and window so a second chart of the same series asks nothing new. The
 *  combined reading is memoised on the fetches, so the plot redraws only
 *  when a series arrives. */
function useChartSeries(chart: SeriesChart, near: boolean): ChartSeries {
  const combine = useCallback(
    (results: UseQueryResult<SeriesPoint[]>[]) => combineSeries(chart, results),
    [chart],
  );
  const points = pointsOf(chart);
  return useQueries({
    combine,
    queries: chart.pipelines.map((pipeline) => ({
      queryKey: ["chat-records-series", pipeline, chart.from, chart.to, points],
      queryFn: async ({ signal }: { signal: AbortSignal }) =>
        seriesPoints(
          await reportsApi.recordsSeries(
            pipeline,
            { from: chart.from, to: chart.to, points },
            signal,
          ),
        ),
      retry: false,
      staleTime: seriesFreshnessMs(chart),
      enabled: near,
    })),
  });
}

/** The engine's word on a series it cannot give — its message when it
 *  sent one, a plain line otherwise. */
function seriesProblem(error: unknown): string {
  if (error instanceof ApiError && error.status === 404) {
    return "The engine holds no record of this pipeline for this window.";
  }
  if (error instanceof ApiError && error.message) return error.message;
  return "The series could not be loaded.";
}

/** The window in words: "from Sep 18, 2026, 15:08 to Sep 28, 2026,
 *  15:08". An edge the fence left to the engine reads as the engine fills
 *  it — the deployment's start, or now. */
function windowWords(chart: ChartFence): string {
  const from = chart.from
    ? `from ${momentLabel(chart.from)}`
    : "from the start of the deployment";
  const to = chart.to ? `to ${momentLabel(chart.to)}` : "to now";
  return `${from} ${to}`;
}

function linesCaption(chart: RecordsChart): string {
  return `${chart.points ?? 30} moments ${windowWords(chart)}. A gap is a moment nobody was watching.`;
}

/** The moment the pie reads, and the window it was the last watched in. */
function shareCaption(chart: PieChart, at: string | undefined): string {
  const window = `the window ${windowWords(chart)}`;
  return at
    ? `Read at ${momentLabel(at)}, the last moment watched in ${window}.`
    : `Nobody was watching in ${window}.`;
}

/** The chart's name when the fence gave none: the pipelines, and what is
 *  drawn of them. */
function defaultTitle(chart: SeriesChart): string {
  const who = chart.pipelines.join(", ");
  if (chart.kind === "pie") {
    const count = chart.pipelines.length === 1 ? "watched" : chart.series[0];
    return `${who}, share of records ${count}`;
  }
  const what =
    chart.series.length === 1 ? `records ${chart.series[0]}` : "records";
  return `${who}, ${what} over time`;
}

/** The engine's word on each pipeline that gave no series, under the
 *  plot when others drew, in its place when none did. */
function Problems({ problems }: { problems: string[] }) {
  return (
    <>
      {problems.map((problem) => (
        <Note key={problem}>{problem}</Note>
      ))}
    </>
  );
}

const NOTHING_WATCHED = "Nothing watched in this window.";

/** A windows chart with nothing to draw: no sample of the store in the
 *  window read it well enough, which is not the same as nothing happening. */
const NO_STORE_READ = "No usable read of the store in this window.";

/** The block the near-viewport hook watches, and the series read for it:
 *  every chart starts the same way — the fetches wait on the block
 *  reaching the viewport, so the hook that watches it and the hook that
 *  fetches belong together. */
function useNearSeries(chart: SeriesChart) {
  const [ref, near] = useNearViewport<HTMLDivElement>();
  return { ref, ...useChartSeries(chart, near) };
}

/** The fence's own name for the chart, or the one its pipelines make. */
function chartTitle(chart: ChartFence): string {
  if (chart.title) return chart.title;
  if (chart.kind === "windows") {
    return `${chart.pipelines.join(", ")}, ${chart.view.replace("_", " ")} over time`;
  }
  return defaultTitle(chart);
}

/** The card both charts draw in. The two differ only in what they
 *  compute — lines or slices — so the shell lives here once: the name, a
 *  skeleton while the fetches are out, the plot with Chart.js suspended
 *  behind the same skeleton, the engine's word on the pipelines that gave
 *  nothing, and the caption. */
interface ChartCardProps {
  /** The card's own block, watched for the viewport by `useNearSeries`. */
  cardRef: RefObject<HTMLDivElement>;
  title: string;
  pending: boolean;
  /** The chart itself, left undefined when no series drew. */
  plot: React.ReactNode;
  /** Whether what drew holds nothing to see. Read only once something
   *  drew, so a caller need not repeat that. */
  empty: boolean;
  /** The engine's word on the pipelines that gave nothing, read under the
   *  plot. */
  notes: string[];
  /** What stands in the plot's place when nothing drew at all: the same
   *  notes, unless the caller has something else to say there. */
  undrawnNotes?: string[];
  /** What an empty plot says; nothing watched unless the caller knows
   *  better. */
  emptyNote?: string;
  caption: string;
}

function ChartCard({
  cardRef,
  title,
  pending,
  plot,
  empty,
  notes,
  undrawnNotes,
  emptyNote = NOTHING_WATCHED,
  caption,
}: ChartCardProps) {
  return (
    <Block ref={cardRef} data-testid="chat-chart">
      <Title>{title}</Title>
      {pending ? (
        <SkeletonBlock $height="220px" />
      ) : plot === undefined ? (
        <Problems problems={undrawnNotes ?? notes} />
      ) : empty ? (
        <Note>{emptyNote}</Note>
      ) : (
        <Plot>
          <Suspense fallback={<SkeletonBlock $height="220px" />}>
            {plot}
          </Suspense>
        </Plot>
      )}
      {plot !== undefined && <Problems problems={notes} />}
      <Note>{caption}</Note>
    </Block>
  );
}

function RecordsChartBlock({ chart }: { chart: RecordsChart }) {
  const { ref, pending, fetched, problems } = useNearSeries(chart);
  const data = useMemo(() => chartLines(chart, fetched), [chart, fetched]);
  const drawn = fetched.size > 0;
  return (
    <ChartCard
      cardRef={ref}
      title={chartTitle(chart)}
      pending={pending}
      plot={drawn ? <LazySeriesChart data={data} /> : undefined}
      empty={!hasCounts(data.lines)}
      notes={problems}
      caption={linesCaption(chart)}
    />
  );
}

/** What the pie has to say besides its slices: the engine's word on each
 *  series that never came, and a line for each pipeline whose series came
 *  watching nothing — named rather than drawn as a slice of nothing. */
function shareNotes(problems: string[], data: ShareData | undefined): string[] {
  return [
    ...problems,
    ...(data?.unread ?? []).map((p) => `${p}: nothing watched in this window.`),
  ];
}

function ShareChartBlock({ chart }: { chart: PieChart }) {
  const { ref, pending, fetched, problems } = useNearSeries(chart);
  const data = useMemo(() => chartSlices(chart, fetched), [chart, fetched]);
  const notes = shareNotes(problems, data);
  return (
    <ChartCard
      cardRef={ref}
      title={chartTitle(chart)}
      pending={pending}
      plot={data === undefined ? undefined : <LazyShareChart data={data} />}
      empty={data !== undefined && !hasShares(data.slices)}
      notes={notes}
      // A pie that read nothing at all says so where the plot would be:
      // every pipeline it names either failed or watched nothing, and
      // with neither to report the window itself was quiet.
      undrawnNotes={notes.length > 0 ? notes : [NOTHING_WATCHED]}
      caption={shareCaption(chart, data?.at)}
    />
  );
}

/** The windows series of the one pipeline the fence names, fetched once the
 *  block nears the viewport, like the records series. */
function useWindowsSeries(chart: WindowsChart) {
  const [ref, near] = useNearViewport<HTMLDivElement>();
  const pipeline = chart.pipelines[0] ?? "";
  const points = chart.shape === "pie" ? undefined : chart.points;
  const query = useQuery({
    queryKey: [
      "chat-windows-series",
      pipeline,
      chart.view,
      chart.from,
      chart.to,
      points,
    ],
    queryFn: ({ signal }) =>
      reportsApi.windowsSeries(
        pipeline,
        chart.view,
        { from: chart.from, to: chart.to, points },
        signal,
      ),
    retry: false,
    staleTime: seriesFreshnessMs(chart),
    enabled: near,
  });
  const problems = query.isError ? [seriesProblem(query.error)] : [];
  // Pending until the series came, off-screen too: a block not yet near the
  // viewport has asked for nothing, which is not the same as reading nothing.
  return { ref, pending: query.isPending, doc: query.data, problems };
}

/** The caption under a windows chart: its moments, its window, and what
 *  the plot cannot show of the store's reads. */
function windowsCaption(
  chart: WindowsChart,
  doc: WindowSeriesJson | undefined,
  readAt: string | undefined,
): string {
  const notes = doc ? windowChartNotes(doc) : [];
  const unit = doc ? `${doc.unit}, ` : "";
  const what =
    chart.shape === "pie"
      ? piePhrase(readAt)
      : `${unit}${chart.points ?? 30} moments`;
  const extra = notes.length > 0 ? ` ${notes.join(". ")}.` : "";
  return `${what} ${windowWords(chart)}. Each moment is the newest sample of the shared store.${extra}`;
}

/** Which moment a pie of partition states read: the last one read in the
 *  window, named by its own time. */
function piePhrase(readAt: string | undefined): string {
  return readAt
    ? `Partition states at ${momentLabel(readAt)}, the last moment read`
    : "Partition states at the last moment read";
}

/** What an empty windows chart says: a pie that read only zeros read
 *  something, and says when; anything else had no usable read. */
function windowsEmptyNote(readAt: string | undefined): string {
  return readAt
    ? `No partition in the chosen states at ${momentLabel(readAt)}.`
    : NO_STORE_READ;
}

/** A windowed pipeline's store over time, as lines, or its partition
 *  states at one moment as a ring. */
/** What a windows chart draws: lines, or a pie of partition states at one
 *  moment; nothing until the series came, or when no moment read the
 *  states. */
type WindowsPlot =
  | { as: "pie"; share: ShareData }
  | { as: "lines"; lines: ReturnType<typeof windowChartData> };

function windowsPlot(
  chart: WindowsChart,
  doc: WindowSeriesJson | undefined,
): WindowsPlot | undefined {
  if (doc === undefined) return undefined;
  if (chart.shape !== "pie") {
    return {
      as: "lines",
      lines: windowChartData(doc, chart.view, chart.series),
    };
  }
  const share = partitionStatesShare(doc, chart.series);
  return share === undefined ? undefined : { as: "pie", share };
}

function windowsPlotElement(plot: WindowsPlot): React.ReactNode {
  return plot.as === "pie" ? (
    <LazyShareChart data={plot.share} />
  ) : (
    <LazySeriesChart data={plot.lines} />
  );
}

function windowsPlotEmpty(plot: WindowsPlot): boolean {
  return plot.as === "pie"
    ? !hasShares(plot.share.slices)
    : !hasCounts(plot.lines.lines);
}

function WindowsChartBlock({ chart }: { chart: WindowsChart }) {
  const { ref, pending, doc, problems } = useWindowsSeries(chart);
  const plot = useMemo(() => windowsPlot(chart, doc), [chart, doc]);
  const readAt = plot?.as === "pie" ? plot.share.at : undefined;
  return (
    <ChartCard
      cardRef={ref}
      title={chartTitle(chart)}
      pending={pending}
      plot={plot && windowsPlotElement(plot)}
      empty={plot !== undefined && windowsPlotEmpty(plot)}
      notes={problems}
      undrawnNotes={problems.length > 0 ? problems : [NO_STORE_READ]}
      emptyNote={windowsEmptyNote(readAt)}
      caption={windowsCaption(chart, doc, readAt)}
    />
  );
}

const Block = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 8px 0;
  padding: 12px;
  border: 1px solid ${(p) => p.theme.cards1Border};
  border-radius: 6px;
  background: ${(p) => p.theme.cards1Background};
`;

const Title = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${(p) => p.theme.semanticTextHeading};
`;

// Room for the plot and, under it, the legend — a row on the Agent page,
// a stack in the Studio sidebar.
const Plot = styled.div`
  position: relative;
  height: 260px;
  min-width: 0;
`;

const Note = styled.span`
  font-size: 12px;
  color: ${(p) => p.theme.semanticTextLight};
`;
